import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';

import {
    PROVINCE,
    useIsMounted,
    LegacyCityPropType,
    MessagePropType,
    QuestionCompact,
    LocationSelector,
    getAffiliateData,
    getLanguageFromLocale,
    AFFILIATE_QUERY_PARAM_KEYS,
    Products,
} from '@ratehub/base-ui';
import {
    GIC_TYPES,
    GIC_TYPES_OPTIONS,
    GIC_TERMS,
    GIC_TERMS_OPTIONS,
    GIC_BALANCE,
    GIC_BALANCE_OPTIONS,
} from '@ratehub/inv-common';

import PAGE_TEMPLATES from '../definitions/PageTemplates';
import GICsAccountPropType from '../definitions/GICsAccountPropType';
import fetchProductsForGICs from '../functions/fetchProductsForGICs';
import useTrackSponsoredProductsHeapEvent from '../hooks/useTrackSponsoredProductsHeapEvent';
import ProductList from './ProductList';
import ProductCardLayoutGICs from './ProductCardLayoutGICs';


function ProductListGICs({
    title,                                        // title is the only required prop
    initialAccountType = GIC_TYPES.REGISTERED,
    initialTerm = GIC_TERMS[4],                   // 12 months
    initialBalance = GIC_BALANCE[1],              // $5_000
    initialProvider,
    initialCity,
    initialProducts = [],

    lastUpdated,
    maxRowsCollapsed = 4,
    maxRowsExpanded,

    ...otherProps
}) {
    const intl = useIntl();
    const getIsMounted = useIsMounted();

    // used to track sponsored GICs onload
    useTrackSponsoredProductsHeapEvent(initialProducts, Products.GICS);

    // TODO: remove workaround after LocationSelector fixed for FilteredAsyncContainer
    // see https://ratehub.atlassian.net/browse/CCDEP-3541
    const [ provinceCode, setProvinceCode ] = useState(initialCity?.province.code || PROVINCE.ONTARIO);
    function updateLocation(values) {
        if (values?.province?.code) {
            setProvinceCode(values.province.code);
        }
    }

    const [ lastUpdatedDate, setLastUpdatedDate ] = useState(lastUpdated);

    const language = getLanguageFromLocale(intl.locale);
    const initialFilterValues = {
        term: initialTerm,
        balance: initialBalance,
        accountType: initialAccountType,
        provinceCode: provinceCode,
        language: language,
        provider: initialProvider,      // when set, this can’t be changed through the filters
    };

    function getFormValuesForFetch(values) {
        return {
            ...initialFilterValues,
            term: values.term,
            balance: values.monthlyBalance,
            accountType: values.accountType,
            provinceCode: values.location?.province.code,
        };
    }


    async function fetchProductGICs(values) {
        const gicParams = {
            ...values,
            isPrioritizeSponsored: true,
            customId: getAffiliateData()?.[AFFILIATE_QUERY_PARAM_KEYS.affiliateId],
        };

        const response = await fetchProductsForGICs(gicParams);

        if (response) {
            if (getIsMounted()) {
                setLastUpdatedDate(response.lastUpdated);
            }
        }

        return response.products;
    }


    return (
        <ProductList
            {...otherProps}
            title={title}
            lastUpdated={lastUpdatedDate}

            maxRowsCollapsed={maxRowsCollapsed}
            maxRowsExpanded={maxRowsExpanded}
            noMatchingItemsMessage={(
                <FormattedMessage
                    id="web-components.gics-product-list.no-matches.label"
                    defaultMessage="No GICs match the search filters."
                />
            )}

            initialFilterValues={initialFilterValues}
            primaryFilters={getGICFilters({
                initialAccountType,
                initialTerm,
                initialBalance,
                initialCity,
                updateLocation,
            })}
            initialFetchedProducts={initialProducts}
            getFormValues={getFormValuesForFetch}
            onFetchValue={fetchProductGICs}
        >
            {(gic, formValues) => {
                return (
                    <ProductCardLayoutGICs
                        gic={gic}
                        balance={formValues.balance}
                        provinceCode={formValues.provinceCode}
                    />
                );
            }}
        </ProductList>
    );
}

ProductListGICs.propTypes = {
    title: MessagePropType.isRequired,

    initialAccountType: PropTypes.oneOf(Object.values(GIC_TYPES)),
    initialTerm: PropTypes.number,
    initialBalance: PropTypes.number,
    initialProvider: PropTypes.string,

    initialCity: LegacyCityPropType,

    initialProducts: PropTypes.arrayOf(GICsAccountPropType),
    lastUpdated: PropTypes.string,

    maxRowsCollapsed: PropTypes.number,
    maxRowsExpanded: PropTypes.number,
};

function getGICFilters({
    initialAccountType,
    initialTerm,
    initialBalance,
    initialCity,
    updateLocation,
}) {
    return [
        <QuestionCompact
            key="accountType"
            field={{
                name: 'accountType',
                type: 'choice',
                value: initialAccountType,
                options: GIC_TYPES_OPTIONS,
            }}
            labelMessage={(
                <FormattedMessage
                    id="web-components.gics-product-list.filters.account-type.label"
                    defaultMessage="Type"
                />
            )}
            type="dropdown"
            className="filter-question-compact"
        />,

        <QuestionCompact
            key="term"
            field={{
                name: 'term',
                type: 'choice',
                value: initialTerm,
                options: GIC_TERMS_OPTIONS,
            }}
            labelMessage={(
                <FormattedMessage
                    id="web-components.gics-product-list.filters.term.label"
                    defaultMessage="Term"
                />
            )}
            type="dropdown"
            className="filter-question-compact"
        />,

        <QuestionCompact
            key="monthlyBalance"
            field={{
                name: 'monthlyBalance',
                type: 'choice',
                value: initialBalance,
                options: GIC_BALANCE_OPTIONS,
            }}
            labelMessage={(
                <FormattedMessage
                    id="web-components.gics-product-list.filters.balance.label"
                    defaultMessage="Balance"
                />
            )}
            type="dropdown"
            className="filter-question-compact"
        />,

        <LocationSelector
            name="location"
            key="location"
            initialProvince={initialCity?.province.code}
            label={(
                <FormattedMessage
                    id="web-components.gics-product-list.location.label"
                    defaultMessage="Location"
                />
            )}
            className="filter-question-compact"
            errorMessageStyle="minimal"
            animationType="none"
            onChange={updateLocation}
        />,
    ];
}


ProductListGICs.blockKey = 'rh/product-list-gics';
ProductListGICs.requiresLayoutRow = true;
ProductListGICs.supportedPageTypes = [ PAGE_TEMPLATES.gics ];

export default observer(ProductListGICs);
